import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, BookOpen, Search, Users, FileText, Upload, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { getYearOptions, getSemesterOptions, STREAMS } from '../utils/yearOptions';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SubjectManagement = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stream: '',
    year: '',
    semester: '',
    subject_type: '',
    category: '',
    credits: '',
    total_classes: ''
  });
  
  const [streamFilter, setStreamFilter] = useState('all');
  
  // Faculty modal state
  const [showFacultyModal, setShowFacultyModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [qualifiedFaculty, setQualifiedFaculty] = useState([]);
  
  // Assignments modal state
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // CSV Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // MBA Year Cleanup modal state
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubject) {
        // Merge with existing subject data to preserve all fields
        const mergedData = {
          ...editingSubject,  // Start with all existing subject data
          ...formData,         // Override with form data
          id: editingSubject.id, // Preserve ID
          total_classes: formData.total_classes ? parseInt(formData.total_classes) : 0
        };
        delete mergedData.category;
        console.log('[UPDATE SUBJECT] Merged data being sent:', mergedData);
        await axios.put(`${API}/subjects/${editingSubject.id}`, mergedData);
        toast.success('Subject updated successfully');
      } else {
        // Prepare data for API (convert total_classes to integer, remove category field)
        const apiData = {
          ...formData,
          total_classes: formData.total_classes ? parseInt(formData.total_classes) : 0
        };
        // Remove category field if it exists (not expected by API)
        delete apiData.category;
        console.log('Creating subject with data:', apiData);
        await axios.post(`${API}/subjects`, apiData);
        toast.success('Subject created successfully');
      }
      
      setIsModalOpen(false);
      setEditingSubject(null);
      setFormData({ name: '', description: '', stream: '', year: '', semester: '', subject_type: '', category: '', credits: '', total_classes: '' });
      fetchSubjects();
    } catch (error) {
      console.error('Error saving subject:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to save subject: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      description: subject.description || '',
      stream: subject.stream || '',
      year: subject.year || '',
      semester: subject.semester || '',
      subject_type: subject.subject_type || '',
      category: subject.category || '',
      credits: subject.credits || '',
      total_classes: subject.total_classes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        await axios.delete(`${API}/subjects/${id}`);
        toast.success('Subject deleted successfully');
        fetchSubjects();
      } catch (error) {
        console.error('Error deleting subject:', error);
        toast.error('Failed to delete subject');
      }
    }
  };

  const handleViewAssignments = async (subject) => {
    setSelectedSubject(subject);
    setShowAssignmentsModal(true);
    setLoadingAssignments(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subjects/${subject.id}/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load assignments');
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImportLoading(true);
    setImportResults(null);

    try {
      // Read and parse CSV file
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        setImportLoading(false);
        return;
      }

      // Parse CSV (expecting: Program, Semester, Course, Assignment Title, Assignment Detail, Marks)
      const headers = lines[0].split(',').map(h => h.trim());
      const assignments = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        
        if (values.length >= 4) {
          const courseName = values[2]; // Course column
          const title = values[3]; // Assignment Title
          const description = values[4] || ''; // Assignment Detail
          const marks = values[5] ? parseFloat(values[5]) : null; // Marks

          if (courseName && title) {
            // Find subject by name (case-insensitive)
            const subject = subjects.find(s => 
              s.name.toLowerCase().includes(courseName.toLowerCase()) ||
              courseName.toLowerCase().includes(s.name.toLowerCase())
            );

            if (subject) {
              assignments.push({
                subject_id: subject.id,
                title: title,
                description: description,
                marks: marks,
                order: i
              });
            }
          }
        }
      }

      if (assignments.length === 0) {
        toast.error('No valid assignments found in CSV. Make sure subject names match existing subjects.');
        setImportLoading(false);
        return;
      }

      // Call bulk import API
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/assignments/bulk-import`,
        { assignments },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setImportResults(response.data);
      toast.success(`Successfully imported ${response.data.created_count} assignments!`);
      
      // Reset file input
      setCsvFile(null);

    } catch (error) {
      console.error('Error importing assignments:', error);
      toast.error(`Import failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleViewFaculty = async (subject) => {
    setSelectedSubject(subject);
    setShowFacultyModal(true);
    setQualifiedFaculty([]);
    
    try {
      const response = await axios.get(`${API}/teachers/by-subject/${subject.id}`);
      setQualifiedFaculty(response.data);
    } catch (error) {
      console.error('Error fetching qualified faculty:', error);
      toast.error('Failed to load qualified faculty');
      setQualifiedFaculty([]);
    }
  };


  const openCreateModal = () => {
    setEditingSubject(null);
    setFormData({ name: '', description: '', stream: '', year: '', semester: '', subject_type: '', category: '', credits: '', total_classes: '' });
    setIsModalOpen(true);
  };

  // One-click MBA year-label normalization.
  // Migrates legacy MBA records ('SY','FY','PG FY','PG SY','PG First Year','PG Second Year')
  // to canonical 'Year 1' / 'Year 2'. Safe to run multiple times.
  const handleMBACleanup = async () => {
    setCleanupLoading(true);
    setCleanupResults(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/fix/normalize-mba-year-labels`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setCleanupResults(response.data);
      const total = (response.data?.subjects?.updated || 0) + (response.data?.batches?.updated || 0);
      if (total > 0) {
        toast.success(`Cleaned up ${total} record(s) — ${response.data.subjects.updated} subjects + ${response.data.batches.updated} batches`);
        fetchSubjects(); // refresh the list
      } else {
        toast.info('Nothing to clean — all MBA records already use canonical labels');
      }
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed. Please try again.');
    } finally {
      setCleanupLoading(false);
    }
  };

  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch = subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (subject.description && subject.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStream = streamFilter === 'all' || subject.stream === streamFilter;
    
    return matchesSearch && matchesStream;
  }).sort((a, b) => {
    // Sort by stream first
    if (a.stream !== b.stream) {
      return a.stream.localeCompare(b.stream);
    }
    
    // Then sort by year
    const yearOrder = { 'FY': 1, 'SY': 2, 'TY': 3, '4th Year': 4, 'Year 1': 1, 'Year 2': 2, 'Year 3': 3, 'Year 4': 4 };
    const yearA = yearOrder[a.year] || 99;
    const yearB = yearOrder[b.year] || 99;
    if (yearA !== yearB) {
      return yearA - yearB;
    }
    
    // Then sort by semester (1-8)
    const semA = parseInt(a.semester?.replace('Semester ', '') || '0');
    const semB = parseInt(b.semester?.replace('Semester ', '') || '0');
    if (semA !== semB) {
      return semA - semB;
    }
    
    // Finally sort by subject name alphabetically
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subject Management</h1>
          <p className="text-gray-600">Manage your course subjects</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => { setCleanupResults(null); setShowCleanupModal(true); }}
            variant="outline"
            className="flex items-center border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="mba-cleanup-btn"
            title="Fix all UG and PG subjects/batches saved with legacy year labels (one-click safe migration)"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Cleanup Year Labels
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)} 
            className="btn-secondary flex items-center"
            data-testid="import-assignments-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Assignments
          </Button>
          <Button onClick={openCreateModal} className="btn-primary flex items-center" data-testid="create-subject-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Subject
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="card-enhanced">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 form-input"
                data-testid="search-subjects"
              />
            </div>
            
            <div>
              <select
                value={streamFilter}
                onChange={(e) => setStreamFilter(e.target.value)}
                className="form-input w-full"
                data-testid="stream-filter"
              >
                <option value="all">All Streams</option>
                {STREAMS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subjects Grid */}
      {filteredSubjects.length === 0 ? (
        <Card className="card-enhanced">
          <CardContent className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No subjects found' : 'No subjects yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? 'Try adjusting your search terms' : 'Create your first subject to get started'}
            </p>
            {!searchTerm && (
              <Button onClick={openCreateModal} className="btn-primary" data-testid="empty-state-create-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create First Subject
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-12">
          {/* Group subjects by stream first, then year and semester */}
          {['Fashion Design', 'Interior Design', 'MBA Design Management', 'Skill Enhancement'].sort().map(stream => {
            const streamSubjects = filteredSubjects.filter(subject => subject.stream === stream);
            if (streamSubjects.length === 0) return null;
            
            return (
              <div key={stream} className="space-y-8">
                <div className="flex items-center justify-between border-b-2 pb-4">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {stream}
                  </h1>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                    stream === 'Interior Design' 
                      ? 'bg-blue-100 text-blue-800' 
                      : stream === 'Fashion Design'
                      ? 'bg-purple-100 text-purple-800'
                      : stream === 'Skill Enhancement'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {streamSubjects.length} subjects
                  </div>
                </div>
                
                {/* Group by year within stream - or flat list for Skill Enhancement */}
                {stream === 'Skill Enhancement' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {streamSubjects.sort((a, b) => a.name.localeCompare(b.name)).map((subject) => (
                      <Card key={subject.id} className="card-enhanced hover-lift" data-testid={`subject-card-${subject.id}`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-sm font-semibold text-gray-900 mb-1 leading-tight">
                                {subject.name}
                              </CardTitle>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {subject.subject_type && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {subject.subject_type}
                                  </span>
                                )}
                                {subject.category && (
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    subject.category === 'Core' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {subject.category}
                                  </span>
                                )}
                                {subject.credits && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {subject.credits}
                                  </span>
                                )}
                              </div>
                              {subject.description && (
                                <p className="text-xs text-gray-600 line-clamp-2">
                                  {subject.description}
                                </p>
                              )}
                            </div>
                            <div className="p-2 bg-amber-50 rounded-lg ml-2">
                              <BookOpen className="w-4 h-4 text-amber-600" />
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                              {subject.stream}
                            </div>
                            <div className="flex space-x-1">
                              <Button variant="outline" size="sm" onClick={() => handleViewFaculty(subject)} className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="View Qualified Faculty" data-testid={`view-faculty-${subject.id}`}>
                                <Users className="w-3 h-3" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleViewAssignments(subject)} className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="View Assignments" data-testid={`assignments-subject-${subject.id}`}>
                                <FileText className="w-3 h-3" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleEdit(subject)} className="p-1 h-6 w-6" data-testid={`edit-subject-${subject.id}`}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleDelete(subject.id)} className="p-1 h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-subject-${subject.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                <>
                {(() => {
                  // Normalize MBA year variants ('SY', 'PG SY', 'PG Second Year' etc.) to a
                  // canonical 'Year 1' / 'Year 2' so subjects saved with different conventions
                  // render under a SINGLE year section, with semesters in natural order.
                  const normalizeMBAYear = (y) => {
                    if (['Year 1', 'PG First Year', 'PG FY', 'FY'].includes(y)) return 'Year 1';
                    if (['Year 2', 'PG Second Year', 'PG SY', 'SY'].includes(y)) return 'Year 2';
                    return y;
                  };
                  const yearOf = (s) => stream === 'MBA Design Management' ? normalizeMBAYear(s.year) : s.year;
                  
                  // Define proper year order for each stream
                  const getYearOrder = (stream) => {
                    if (stream === 'MBA Design Management') {
                      return ['Year 1', 'Year 2'];
                    } else {
                      return ['FY', 'SY', 'TY', 'Fourth Year', 'PG First Year', 'PG Second Year'];
                    }
                  };
                  
                  const yearOrder = getYearOrder(stream);
                  const availableYears = [...new Set(streamSubjects.map(yearOf))];
                  const orderedYears = yearOrder.filter(year => availableYears.includes(year));
                  // Append any year not covered by the predefined order so subjects never silently vanish
                  const otherYears = availableYears.filter(y => y && !yearOrder.includes(y));
                  return [...orderedYears, ...otherYears];
                })().map(year => {
                  const normalizeMBAYear = (y) => {
                    if (['Year 1', 'PG First Year', 'PG FY', 'FY'].includes(y)) return 'Year 1';
                    if (['Year 2', 'PG Second Year', 'PG SY', 'SY'].includes(y)) return 'Year 2';
                    return y;
                  };
                  const yearSubjects = streamSubjects.filter(subject => 
                    (stream === 'MBA Design Management' ? normalizeMBAYear(subject.year) : subject.year) === year
                  );
            if (yearSubjects.length === 0) return null;
            
            return (
              <div key={year} className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">
                  {year === 'FY' ? 'First Year' : 
                   year === 'SY' ? 'Second Year' : 
                   year === 'TY' ? 'Third Year' : 
                   year === 'Fourth Year' ? 'Fourth Year' : 
                   year === 'PG First Year' ? 'PG First Year' :
                   year === 'PG Second Year' ? 'PG Second Year' :
                   year}
                </h2>
                
                {/* Group by semester within year */}
                {(() => {
                  // Define proper semester order for each year.
                  // MBA year is already normalized to 'Year 1' / 'Year 2' upstream.
                  const getSemesterOrder = (year, stream) => {
                    if (stream === 'MBA Design Management') {
                      if (year === 'Year 1') return ['PG Sem 1', 'PG Sem 2'];
                      if (year === 'Year 2') return ['PG Sem 3', 'PG Sem 4'];
                    } else {
                      // For Fashion Design and Interior Design
                      if (year === 'FY') return ['Semester 1', 'Semester 2'];
                      if (year === 'SY') return ['Semester 3', 'Semester 4'];
                      if (year === 'TY') return ['Semester 5', 'Semester 6'];
                      if (year === 'Fourth Year') return ['Semester 7', 'Semester 8'];
                      if (year === 'PG First Year') return ['PG Sem 1', 'PG Sem 2'];
                      if (year === 'PG Second Year') return ['PG Sem 3', 'PG Sem 4'];
                    }
                    return [];
                  };
                  
                  const semesterOrder = getSemesterOrder(year, stream);
                  const availableSemesters = [...new Set(yearSubjects.map(s => s.semester))];
                  const orderedSemesters = semesterOrder.filter(semester => availableSemesters.includes(semester));
                  // Append any semester not in predefined order so subjects never silently vanish
                  const otherSemesters = availableSemesters.filter(s => s && !semesterOrder.includes(s));
                  return [...orderedSemesters, ...otherSemesters];
                })().map(semester => {
                  const semesterSubjects = yearSubjects.filter(subject => subject.semester === semester);
                  if (semesterSubjects.length === 0) return null;
                  
                  return (
                    <div key={semester} className="space-y-3">
                      <h3 className="text-lg font-semibold text-blue-800 ml-4">{semester}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-8">
                        {semesterSubjects.sort((a, b) => a.name.localeCompare(b.name)).map((subject) => (
                          <Card key={subject.id} className="card-enhanced hover-lift" data-testid={`subject-card-${subject.id}`}>
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <CardTitle className="text-sm font-semibold text-gray-900 mb-1 leading-tight">
                                    {subject.name}
                                  </CardTitle>
                                  
                                  {/* Subject metadata */}
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {subject.subject_type && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                        {subject.subject_type}
                                      </span>
                                    )}
                                    {subject.category && (
                                      <span className={`text-xs px-2 py-1 rounded ${
                                        subject.category === 'Core' 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-orange-100 text-orange-700'
                                      }`}>
                                        {subject.category}
                                      </span>
                                    )}
                                    {subject.credits && (
                                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                        {subject.credits}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {subject.description && (
                                    <p className="text-xs text-gray-600 line-clamp-2">
                                      {subject.description}
                                    </p>
                                  )}
                                </div>
                                <div className="p-2 bg-green-50 rounded-lg ml-2">
                                  <BookOpen className="w-4 h-4 text-green-600" />
                                </div>
                              </div>
                            </CardHeader>
                            
                            <CardContent className="pt-0">
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-gray-500">
                                  {[subject.stream, subject.year, subject.semester].filter(Boolean).join(' • ')}
                                </div>
                                
                                <div className="flex space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewFaculty(subject)}
                                    className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="View Qualified Faculty"
                                    data-testid={`view-faculty-${subject.id}`}
                                  >
                                    <Users className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewAssignments(subject)}
                                    className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="View Assignments"
                                    data-testid={`assignments-subject-${subject.id}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEdit(subject)}
                                    className="p-1 h-6 w-6"
                                    data-testid={`edit-subject-${subject.id}`}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(subject.id)}
                                    className="p-1 h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-subject-${subject.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
                  </div>
                );
              })}
              </>
              )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="subject-modal-title">
              {editingSubject ? 'Edit Subject' : 'Create New Subject'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                placeholder="Enter subject name"
                required
                data-testid="subject-name-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input resize-none"
                rows="3"
                placeholder="Enter subject description (optional)"
                data-testid="subject-description-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stream *
              </label>
              <select
                value={formData.stream}
                onChange={(e) => {
                  const newStream = e.target.value;
                  if (newStream === 'Skill Enhancement') {
                    setFormData({ ...formData, stream: newStream, year: '', semester: '' });
                  } else {
                    setFormData({ ...formData, stream: newStream });
                  }
                }}
                className="form-input"
                required
                data-testid="subject-stream-select"
              >
                <option value="">Select Stream</option>
                {STREAMS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            
            {formData.stream !== 'Skill Enhancement' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value, semester: '' })}
                  className="form-input"
                  data-testid="subject-year-select"
                  disabled={!formData.stream}
                >
                  <option value="">{formData.stream ? 'Select Year' : 'Select Stream first'}</option>
                  {getYearOptions(formData.stream).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Semester
                </label>
                <select
                  value={formData.semester}
                  onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                  className="form-input"
                  data-testid="subject-semester-select"
                  disabled={!formData.year}
                >
                  <option value="">{formData.year ? 'Select Semester' : 'Select Year first'}</option>
                  {getSemesterOptions(formData.stream, formData.year).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Type
                </label>
                <select
                  value={formData.subject_type}
                  onChange={(e) => setFormData({ ...formData, subject_type: e.target.value })}
                  className="form-input"
                  data-testid="subject-type-select"
                >
                  <option value="">Select Type</option>
                  <option value="Theory">Theory</option>
                  <option value="Practical">Practical</option>
                  <option value="Theory/Practical">Theory/Practical</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="form-input"
                  data-testid="subject-category-select"
                >
                  <option value="">Select Category</option>
                  <option value="Core">Core</option>
                  <option value="General Education">General Education</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credits (e.g., 2-0, 0-4)
                </label>
                <input
                  type="text"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                  className="form-input"
                  placeholder="Enter credits (optional)"
                  data-testid="subject-credits-input"
                />
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Classes Required
                </label>
                <input
                  type="number"
                  value={formData.total_classes}
                  onChange={(e) => setFormData({ ...formData, total_classes: e.target.value })}
                  className="form-input"
                  placeholder="e.g., 15"
                  min="1"
                  max="100"
                  data-testid="subject-total-classes-input"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
                data-testid="cancel-subject-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary"
                data-testid="save-subject-btn"
              >
                {editingSubject ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Qualified Faculty Modal */}
      <Dialog open={showFacultyModal} onOpenChange={setShowFacultyModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="faculty-modal-title">
              Qualified Faculty - {selectedSubject?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {qualifiedFaculty.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No faculty assigned to this subject yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {qualifiedFaculty.map((teacher) => (
                  <Card key={teacher.id} className="card-enhanced">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{teacher.name}</h4>
                          {teacher.email && (
                            <p className="text-sm text-gray-600 mt-1">
                              Email: {teacher.email}
                            </p>
                          )}
                          {teacher.phone && (
                            <p className="text-sm text-gray-600">
                              Phone: {teacher.phone}
                            </p>
                          )}
                          <div className="mt-2">
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              {teacher.subjects?.length || 0} subjects assigned
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            <div className="flex justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFacultyModal(false)}
                data-testid="close-faculty-modal-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignments Modal */}
      <Dialog open={showAssignmentsModal} onOpenChange={setShowAssignmentsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="assignments-modal-title">
              Assignments - {selectedSubject?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No assignments found for this subject</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment) => (
                  <Card key={assignment.id} className="card-enhanced">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{assignment.teacher_name}</h4>
                          {assignment.teacher_email && (
                            <p className="text-sm text-gray-600 mt-1">
                              Email: {assignment.teacher_email}
                            </p>
                          )}
                          {assignment.assigned_date && (
                            <p className="text-sm text-gray-600">
                              Assigned: {new Date(assignment.assigned_date).toLocaleDateString()}
                            </p>
                          )}
                          <div className="mt-2">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              Active Assignment


      {/* Import Assignments Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="import-modal-title" className="text-2xl font-bold text-red-600">
              🔴🔴🔴 TEST BUILD v3 - IMPORT ASSIGNMENTS 🔴🔴🔴
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">📋 CSV Format Requirements:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Column 1:</strong> Program (e.g., "B.Des")</li>
                <li>• <strong>Column 2:</strong> Semester (e.g., "Semester 1")</li>
                <li>• <strong>Column 3:</strong> Course/Subject Name (must match existing subject)</li>
                <li>• <strong>Column 4:</strong> Assignment Title</li>
                <li>• <strong>Column 5:</strong> Assignment Description (optional)</li>
                <li>• <strong>Column 6:</strong> Marks (optional, numeric)</li>
              </ul>
              <p className="text-xs text-blue-700 mt-2">
                💡 <strong>Tip:</strong> The system will auto-match course names to existing subjects
              </p>
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select CSV File
                </label>
                <a
                  href="data:text/csv;charset=utf-8,Program,Semester,Course,Assignment Title,Assignment Detail,Marks%0AB.Des,Semester 1,Basics of fashion Design,Assignment 1,Make a power point presentation on elements of design,10"
                  download="assignment_template.csv"
                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded font-medium"
                >
                  📥 Download Template
                </a>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setCsvFile(e.target.files[0]);
                  setImportResults(null);
                }}
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
                data-testid="csv-file-input"
              />
              {csvFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Selected: {csvFile.name}
                </p>
              )}
            </div>

            {importResults && (
              <div className={`border rounded-lg p-4 ${
                importResults.created_count > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <h4 className={`font-medium mb-2 ${
                  importResults.created_count > 0 ? 'text-green-900' : 'text-red-900'
                }`}>
                  Import Results:
                </h4>
                <p className={`text-sm ${
                  importResults.created_count > 0 ? 'text-green-800' : 'text-red-800'
                }`}>
                  ✓ Successfully created <strong>{importResults.created_count}</strong> assignments
                </p>
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-red-300">
                    <p className="text-sm text-red-900 font-medium mb-1">⚠️ Errors ({importResults.errors.length}):</p>
                    <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {importResults.errors.slice(0, 10).map((error, index) => (
                        <li key={index} className="pl-2">• {error}</li>
                      ))}
                      {importResults.errors.length > 10 && (
                        <li className="pl-2 italic">... and {importResults.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setCsvFile(null);
                  setImportResults(null);
                }}
                className="flex-1"
                disabled={importLoading}
                data-testid="cancel-import-btn"
              >
                {importResults ? 'Close' : 'Cancel'}
              </Button>
              <Button
                type="button"
                onClick={handleImportCSV}
                disabled={!csvFile || importLoading}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-csv-btn"
              >
                {importLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            <div className="flex justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAssignmentsModal(false)}
                data-testid="close-assignments-modal-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Assignments Modal */}
      <Dialog open={showAssignmentsModal} onOpenChange={setShowAssignmentsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="assignments-modal-title">
              📚 Assignments - {selectedSubject?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 ml-3">Loading assignments...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 text-lg font-medium">No assignments found</p>
                <p className="text-gray-500 text-sm mt-1">This subject doesn't have any assignments yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>{assignments.length}</strong> assignment{assignments.length !== 1 ? 's' : ''} found for this subject
                  </p>
                </div>
                
                {assignments.map((assignment, index) => (
                  <Card key={assignment.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-semibold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2">{assignment.title}</h4>
                          
                          {assignment.description && (
                            <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                              {assignment.description}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            {assignment.marks && (
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                                {assignment.marks} marks
                              </span>
                            )}
                            <span className="text-gray-400">
                              Order: {assignment.order}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAssignmentsModal(false);
                  setSelectedSubject(null);
                  setAssignments([]);
                }}
                data-testid="close-assignments-modal-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Assignments Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="import-modal-title">
              Import Assignments from CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Column 1: Program (e.g., "Fashion Design")</li>
                <li>• Column 2: Semester (e.g., "Semester 1")</li>
                <li>• Column 3: Course/Subject Name</li>
                <li>• Column 4: Assignment Title</li>
                <li>• Column 5: Assignment Description (optional)</li>
                <li>• Column 6: Marks (optional)</li>
              </ul>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="form-input"
                data-testid="csv-file-input"
              />
            </div>

            {importResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">Import Results:</h4>
                <p className="text-sm text-green-800">
                  Successfully created {importResults.created_count} assignments
                </p>
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-red-800 font-medium">Errors:</p>
                    <ul className="text-sm text-red-700 mt-1">
                      {importResults.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setCsvFile(null);
                  setImportResults(null);
                }}
                className="flex-1"
                data-testid="cancel-import-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleImportCSV}
                disabled={!csvFile || importLoading}
                className="flex-1 btn-primary"
                data-testid="import-csv-btn"
              >
                {importLoading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Year Labels Cleanup Modal */}
      <Dialog open={showCleanupModal} onOpenChange={setShowCleanupModal}>
        <DialogContent className="sm:max-w-lg" data-testid="mba-cleanup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center text-amber-700">
              <Wrench className="w-5 h-5 mr-2" />
              Cleanup Year Labels (UG + PG)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
              <p className="font-semibold mb-2">What this does:</p>
              <p>1️⃣ Repairs any subject or batch in the DB that's missing its <code>id</code> field (rare but causes records to silently disappear from dropdowns)</p>
              <p className="mt-2">2️⃣ Re-derives the <code>year</code> field of every subject + batch from its <code>semester</code> value so all records use canonical labels:</p>
              <ul className="list-disc ml-5 mt-2 space-y-0.5 text-amber-800 text-xs">
                <li><strong>UG</strong> (any stream): Semester 1+2 → <code>FY</code> · 3+4 → <code>SY</code> · 5+6 → <code>TY</code> · 7+8 → <code>Fourth Year</code></li>
                <li><strong>MBA PG</strong>: PG Sem 1+2 → <code>Year 1</code> · 3+4 → <code>Year 2</code></li>
                <li><strong>Fashion / Interior PG</strong>: PG Sem 1+2 → <code>PG First Year</code> · 3+4 → <code>PG Second Year</code></li>
              </ul>
              <p className="mt-3 text-xs">✓ Skill Enhancement and records without semester are skipped.</p>
              <p className="text-xs">✓ Safe to run multiple times — only changes records that need changing.</p>
            </div>

            {cleanupResults && (
              <div className="border border-green-200 bg-green-50 rounded-lg p-4 text-sm" data-testid="cleanup-results">
                <p className="font-semibold text-green-800 mb-2">✅ Cleanup completed</p>
                <p className="text-green-700">
                  Subjects: <strong>{cleanupResults.subjects?.updated || 0}</strong> year-label updates,
                  {' '}<strong>{cleanupResults.subjects?.ids_fixed || 0}</strong> missing IDs repaired
                </p>
                <p className="text-green-700">
                  Batches: <strong>{cleanupResults.batches?.updated || 0}</strong> year-label updates,
                  {' '}<strong>{cleanupResults.batches?.ids_fixed || 0}</strong> missing IDs repaired
                </p>
                {((cleanupResults.subjects?.samples?.length || 0) > 0) && (
                  <details className="mt-3 text-xs text-green-700">
                    <summary className="cursor-pointer font-medium">View sample changes</summary>
                    <ul className="mt-1 ml-4 list-disc space-y-0.5">
                      {cleanupResults.subjects.samples.slice(0, 10).map(s => (
                        <li key={s.id}>
                          {s.name}: {s.note ? <em>{s.note}</em> : <><code>{s.old_year}</code> → <code>{s.new_year}</code></>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCleanupModal(false)}
              disabled={cleanupLoading}
              data-testid="cleanup-cancel-btn"
            >
              {cleanupResults ? 'Close' : 'Cancel'}
            </Button>
            {!cleanupResults && (
              <Button
                onClick={handleMBACleanup}
                disabled={cleanupLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="cleanup-confirm-btn"
              >
                {cleanupLoading ? 'Running...' : 'Run Cleanup'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SubjectManagement;